<template>
  <div class="min-h-screen bg-[#f8fafa]">
    <!-- Top bar -->
    <div class="bg-[#191c1e] py-2.5">
      <div
        class="max-w-[1280px] mx-auto px-6 flex items-center justify-between"
      >
        <div class="flex items-center gap-2.5">
          <Link
            href="/"
            class="font-[Poppins] font-bold text-[11px] text-teal-400 tracking-[1.2px] uppercase no-underline hover:text-teal-300 transition-colors"
            >Emmalagat</Link
          >
          <span class="text-[#3c4947]">/</span>
          <span
            class="font-[Poppins] font-bold text-[11px] text-white/50 tracking-[1.2px] uppercase"
            >Designsystem</span
          >
        </div>
        <span class="font-[Plus_Jakarta_Sans] text-[11px] text-white/30"
          >{{ SECTIONS.length }} komponenter · v1.0 · 2026</span
        >
      </div>
    </div>

    <div class="max-w-[1280px] mx-auto flex min-h-[calc(100vh-42px)]">
      <!-- Desktop sidebar -->
      <aside
        class="hidden md:flex flex-col w-64 shrink-0 pt-6 pb-8 border-r border-[#eceef0] bg-white sticky top-[42px] self-start h-[calc(100vh-42px)] overflow-y-auto"
      >
        <div v-for="group in GROUPS" :key="group" class="mb-2">
          <div class="px-5 pt-2 pb-1">
            <p
              class="font-[Poppins] font-bold text-[9px] text-[#c0cac8] tracking-[1.4px] uppercase m-0"
            >
              {{ group }}
            </p>
          </div>
          <button
            v-for="s in SECTIONS.filter((x) => x.group === group)"
            :key="s.id"
            class="flex items-center gap-2.5 w-full px-5 py-2 border-none cursor-pointer text-left transition-all duration-[120ms]"
            :class="
              activeSection === s.id
                ? 'bg-teal-500/[0.07] border-l-[3px] border-l-teal-500'
                : 'border-l-[3px] border-l-transparent bg-transparent'
            "
            @click="handleSelect(s.id)"
          >
            <span class="text-[13px] w-[18px] text-center shrink-0">{{
              s.emoji
            }}</span>
            <span
              class="font-[Plus_Jakarta_Sans] text-[13px]"
              :class="
                activeSection === s.id
                  ? 'font-bold text-[#191c1e]'
                  : 'font-normal text-[#6c7a77]'
              "
              >{{ s.label }}</span
            >
          </button>
        </div>
      </aside>

      <!-- Mobile picker -->
      <div class="md:hidden w-full">
        <div
          class="px-4 py-3 border-b border-[#eceef0] bg-white sticky top-[42px] z-10"
        >
          <button
            class="flex items-center justify-between w-full px-4 py-2.5 rounded-xl bg-[#f2f4f6] border-none cursor-pointer"
            @click="mobileOpen = !mobileOpen"
          >
            <span class="flex items-center gap-2">
              <span class="text-sm">{{ activeSectionItem?.emoji }}</span>
              <span
                class="font-[Plus_Jakarta_Sans] font-bold text-[15px] text-[#191c1e]"
                >{{ activeSectionItem?.label }}</span
              >
            </span>
            <ChevronUp v-if="mobileOpen" :size="16" color="#6c7a77" />
            <ChevronDown v-else :size="16" color="#6c7a77" />
          </button>
          <div
            v-if="mobileOpen"
            class="mt-2 bg-white rounded-2xl border border-[#eceef0] overflow-hidden max-h-[60vh] overflow-y-auto"
          >
            <div v-for="group in GROUPS" :key="group">
              <div class="px-4 pt-2 pb-1 bg-[#f8fafa]">
                <p
                  class="font-[Poppins] font-bold text-[9px] text-[#a0b0ae] tracking-[1.4px] uppercase m-0"
                >
                  {{ group }}
                </p>
              </div>
              <button
                v-for="s in SECTIONS.filter((x) => x.group === group)"
                :key="s.id"
                class="flex items-center gap-2.5 w-full px-4 py-2.5 border-none border-b border-b-[#f0f2f4] cursor-pointer text-left"
                :class="
                  activeSection === s.id ? 'bg-teal-500/[0.07]' : 'bg-white'
                "
                @click="handleSelect(s.id)"
              >
                <span class="text-[13px]">{{ s.emoji }}</span>
                <span
                  class="font-[Plus_Jakarta_Sans] text-sm"
                  :class="
                    activeSection === s.id
                      ? 'font-bold text-teal-500'
                      : 'font-normal text-[#3c4947]'
                  "
                  >{{ s.label }}</span
                >
              </button>
            </div>
          </div>
        </div>
        <!-- Mobile content -->
        <div class="p-8">
          <component :is="activeSectionComponent" v-bind="sectionProps" />
        </div>
      </div>

      <!-- Desktop main content -->
      <main class="hidden md:block flex-1 px-14 py-12 min-w-0">
        <component :is="activeSectionComponent" v-bind="sectionProps" />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineOptions } from "vue";
import { Link } from "@inertiajs/vue3";
import { ChevronDown, ChevronUp } from "lucide-vue-next";

defineOptions({ layout: null });

/* ─── Section registry ─────────────────────────────────── */
const SECTIONS = [
  { id: "intro", label: "Introduktion", emoji: "👋", group: "Grund" },
  { id: "logotype", label: "Logotyp", emoji: "◦", group: "Grund" },
  { id: "colors", label: "Färger", emoji: "🎨", group: "Grund" },
  { id: "typography", label: "Typografi", emoji: "Aa", group: "Grund" },
  { id: "icons", label: "Ikoner", emoji: "✦", group: "Grund" },
  { id: "spacing", label: "Avstånd & Rundning", emoji: "▦", group: "Grund" },
  { id: "recipe-card", label: "Receptkort", emoji: "🃏", group: "Recept" },
  { id: "badges", label: "Märken", emoji: "🏷", group: "Recept" },
  { id: "difficulty", label: "Svårighetsnivå", emoji: "📊", group: "Recept" },
  { id: "like", label: "Gilla-knapp", emoji: "♡", group: "Recept" },
  {
    id: "recipe-step",
    label: "Receptsteg",
    emoji: "①",
    group: "Recept",
  },
  {
    id: "ingredient-item",
    label: "Ingrediensrad",
    emoji: "⊟",
    group: "Recept",
  },
  { id: "buttons", label: "Knappar", emoji: "⬡", group: "Form & interaktion" },
  { id: "search", label: "Sök", emoji: "🔍", group: "Form & interaktion" },
  {
    id: "filters",
    label: "Filterrad",
    emoji: "⚙",
    group: "Form & interaktion",
  },
  {
    id: "slider",
    label: "Skjutreglage",
    emoji: "⟷",
    group: "Form & interaktion",
  },
  {
    id: "number-input",
    label: "Stepper",
    emoji: "±",
    group: "Form & interaktion",
  },
  {
    id: "segmented",
    label: "Flikväljare",
    emoji: "▣",
    group: "Form & interaktion",
  },
  {
    id: "label-tooltip",
    label: "Tooltip",
    emoji: "?",
    group: "Form & interaktion",
  },
  {
    id: "form-fields",
    label: "Formulärfält",
    emoji: "✎",
    group: "Form & interaktion",
  },
  {
    id: "result-row",
    label: "Resultatrad",
    emoji: "=",
    group: "Data & resultat",
  },
  {
    id: "bar-chart",
    label: "Stapeldiagram",
    emoji: "▮",
    group: "Data & resultat",
  },
  {
    id: "tip-callout",
    label: "Tips",
    emoji: "💡",
    group: "Artikelblock",
  },
  {
    id: "warning-callout",
    label: "Varning",
    emoji: "⚠",
    group: "Artikelblock",
  },
  { id: "pullquote", label: "Citatblock", emoji: "❝", group: "Artikelblock" },
  {
    id: "keypoints",
    label: "Nyckelpunkter",
    emoji: "✓",
    group: "Artikelblock",
  },
  {
    id: "image-caption",
    label: "Bild med bildtext",
    emoji: "🖼",
    group: "Artikelblock",
  },
  {
    id: "breadcrumbs",
    label: "Brödsmulor",
    emoji: "›",
    group: "Layout & nav",
  },
  {
    id: "navbar",
    label: "Navbar",
    emoji: "≡",
    group: "Layout & nav",
  },
  {
    id: "footer",
    label: "Footer",
    emoji: "□",
    group: "Layout & nav",
  },
  {
    id: "glass-pill",
    label: "Bakåtknapp",
    emoji: "←",
    group: "Layout & nav",
  },
  {
    id: "section-heading",
    label: "Avsnittsrubrik",
    emoji: "—",
    group: "Layout & nav",
  },
  {
    id: "overlay-card",
    label: "Bildkort",
    emoji: "🏔",
    group: "Layout & nav",
  },
  {
    id: "horizontal-card",
    label: "Horisontellt kort",
    emoji: "▷",
    group: "Layout & nav",
  },
  {
    id: "sidebar-panel",
    label: "Sidopanel",
    emoji: "▱",
    group: "Layout & nav",
  },
  {
    id: "technique-card",
    label: "Teknik-kort",
    emoji: "📋",
    group: "Layout & nav",
  },
];

const GROUPS = [...new Set(SECTIONS.map((s) => s.group))];

/* ─── Navigation state ─────────────────────────────────── */
const activeSection = ref("intro");
const mobileOpen = ref(false);

const activeSectionItem = computed(() =>
  SECTIONS.find((s) => s.id === activeSection.value),
);

function handleSelect(id) {
  activeSection.value = id;
  mobileOpen.value = false;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/* ─── Dynamic component routing ────────────────────────── */
import SectionIntro from "./sections/Intro.vue";
import SectionColors from "./sections/Colors.vue";
import SectionTypography from "./sections/Typography.vue";
import SectionIcons from "./sections/Icons.vue";
import SectionSpacing from "./sections/Spacing.vue";
import SectionRecipeCard from "./sections/RecipeCard.vue";
import SectionBadges from "./sections/Badges.vue";
import SectionDifficulty from "./sections/Difficulty.vue";
import SectionLike from "./sections/Like.vue";
import SectionButtons from "./sections/Buttons.vue";
import SectionSearch from "./sections/Search.vue";
import SectionFilters from "./sections/Filters.vue";
import SectionSlider from "./sections/Slider.vue";
import SectionNumberInput from "./sections/NumberInput.vue";
import SectionSegmented from "./sections/Segmented.vue";
import SectionLabelTooltip from "./sections/LabelTooltip.vue";
import SectionResultRow from "./sections/ResultRow.vue";
import SectionBarChart from "./sections/BarChart.vue";
import SectionTipCallout from "./sections/TipCallout.vue";
import SectionWarningCallout from "./sections/WarningCallout.vue";
import SectionPullquote from "./sections/Pullquote.vue";
import SectionKeypoints from "./sections/Keypoints.vue";
import SectionImageCaption from "./sections/ImageCaption.vue";
import SectionGlassPill from "./sections/GlassPill.vue";
import SectionHeadingDivider from "./sections/HeadingDivider.vue";
import SectionOverlayCard from "./sections/OverlayCard.vue";
import SectionHorizontalCard from "./sections/HorizontalCard.vue";
import SectionSidebarPanel from "./sections/SidebarPanel.vue";
import SectionFormFields from "./sections/FormFields.vue";
import SectionBreadcrumbs from "./sections/Breadcrumbs.vue";
import SectionRecipeStep from "./sections/RecipeStep.vue";
import SectionIngredientItem from "./sections/IngredientItem.vue";
import SectionNavbar from "./sections/Navbar.vue";
import SectionFooter from "./sections/Footer.vue";
import SectionLogotype from "./sections/Logotype.vue";
import SectionTechniqueCard from "./sections/TechniqueCard.vue";

const SECTION_MAP = {
  intro: SectionIntro,
  colors: SectionColors,
  typography: SectionTypography,
  icons: SectionIcons,
  spacing: SectionSpacing,
  "recipe-card": SectionRecipeCard,
  badges: SectionBadges,
  difficulty: SectionDifficulty,
  like: SectionLike,
  buttons: SectionButtons,
  search: SectionSearch,
  filters: SectionFilters,
  slider: SectionSlider,
  "number-input": SectionNumberInput,
  segmented: SectionSegmented,
  "label-tooltip": SectionLabelTooltip,
  "result-row": SectionResultRow,
  "bar-chart": SectionBarChart,
  "tip-callout": SectionTipCallout,
  "warning-callout": SectionWarningCallout,
  pullquote: SectionPullquote,
  keypoints: SectionKeypoints,
  "image-caption": SectionImageCaption,
  "glass-pill": SectionGlassPill,
  "section-heading": SectionHeadingDivider,
  "overlay-card": SectionOverlayCard,
  "horizontal-card": SectionHorizontalCard,
  "sidebar-panel": SectionSidebarPanel,
  "form-fields": SectionFormFields,
  breadcrumbs: SectionBreadcrumbs,
  "recipe-step": SectionRecipeStep,
  "ingredient-item": SectionIngredientItem,
  navbar: SectionNavbar,
  footer: SectionFooter,
  logotype: SectionLogotype,
  "technique-card": SectionTechniqueCard,
};

const activeSectionComponent = computed(() => SECTION_MAP[activeSection.value]);

// Only pass truly shared/static props; each section owns its own interactive state
const sectionProps = computed(() => ({
  sectionCount: SECTIONS.length,
}));
</script>
